import { Link, useLocation } from 'react-router-dom'

// Sticky top navigation mirroring jnati.com marketing (#7r2).
// Six items: HOME, FEATURES, TESTIMONIALS, ABOUT, CONTACT US, BLOG.
// First four are in-page anchor hash links; HOME goes to /; BLOG is
// disabled with "(coming soon)" until the app ships a blog (per
// dispatch override).
//
// Mobile: no hamburger — items flex-wrap and center-justify, matching
// marketing's idiom.
//
// Anchor links only resolve when we're on a page that has the target
// section. On /signup or /forgot-password, clicking FEATURES would
// navigate to /signup#features which doesn't exist there. Resolution:
// anchor links always route via /login#features (the section anchors
// live on the landing-form Login page). Single source of truth.

interface NavItem {
  label: string
  to: string
  disabled?: boolean
}

// HOME points at the landing root (which redirects unauth → /login).
// Section anchors hardcode /login as the host page so they work from
// /signup or /forgot-password too.
const NAV_ITEMS: NavItem[] = [
  { label: 'HOME',         to: '/' },
  { label: 'FEATURES',     to: '/login#features' },
  { label: 'TESTIMONIALS', to: '/login#testimonials' },
  { label: 'ABOUT',        to: '/login#about' },
  { label: 'CONTACT US',   to: '/login#contact' },
  { label: 'BLOG',         to: '/blog', disabled: true },
]

export function TopNav() {
  const location = useLocation()
  const onLogin = location.pathname === '/' || location.pathname === '/login'

  return (
    <header className="sticky top-0 z-50 bg-navy shadow-[0_4px_8px_rgba(0,0,0,0.6)]">
      <nav
        className="max-w-6xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap"
        aria-label="Primary"
      >
        <Link to="/login" className="flex items-center shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-gold/60 rounded">
          <img src="/logo-wide.png" alt="jnati" className="h-8 sm:h-9 w-auto select-none" draggable={false} />
        </Link>
        <ul className="flex items-center gap-1 sm:gap-2 flex-wrap justify-center">
          {NAV_ITEMS.map((item) => {
            if (item.disabled) {
              return (
                <li key={item.label}>
                  <span
                    className="inline-flex items-center text-white/50 text-xs sm:text-sm font-semibold px-2 sm:px-3 py-1.5 cursor-not-allowed"
                    title="Coming soon"
                    aria-disabled="true"
                  >
                    {item.label}
                    <span className="ml-1 text-[10px] uppercase tracking-wider hidden sm:inline">(soon)</span>
                  </span>
                </li>
              )
            }
            // For section anchors, when already on /login use the pure
            // hash so the browser scrolls in-page; otherwise route to
            // /login#section which the landing handles on mount.
            const href = onLogin && item.to.startsWith('/login#')
              ? item.to.slice('/login'.length)
              : item.to
            // Plain anchor for pure hash on same page; Link for routing.
            if (href.startsWith('#')) {
              return (
                <li key={item.label}>
                  <a
                    href={href}
                    className="inline-block text-white hover:text-gold text-xs sm:text-sm font-semibold px-2 sm:px-3 py-1.5 rounded outline-none focus-visible:ring-2 focus-visible:ring-gold/60 transition-colors"
                  >
                    {item.label}
                  </a>
                </li>
              )
            }
            return (
              <li key={item.label}>
                <Link
                  to={href}
                  className="inline-block text-white hover:text-gold text-xs sm:text-sm font-semibold px-2 sm:px-3 py-1.5 rounded outline-none focus-visible:ring-2 focus-visible:ring-gold/60 transition-colors"
                >
                  {item.label}
                </Link>
              </li>
            )
          })}
        </ul>
      </nav>
    </header>
  )
}
